import { Link } from 'react-router-dom'
import { LoginForm } from '../components/LoginForm'
import type { Reckoning } from '../calendar'
import { AppointedTimeCard, AppointedTimes, MonthWidget } from '../shell/Widgets'
import { CleanCheckDemo } from './CleanCheckDemo'

/**
 * The public front door, inside the same shell a member sees.
 *
 * Since 2026-08-10 the app says nothing about membership, so this is the only place anyone can
 * learn Covellah exists or decide to pay for it. A stranger arrives in the members' site itself:
 * the calendar in the ribbon and sidebar is live, the Leviticus 11 check below runs for real,
 * and the sign-in form sits where a member would look for it.
 *
 * Deliberately absent, because PRODUCT.md records these as absences and a front door is where
 * they get invented: no testimonials, no member count, no press, no photography of food, and no
 * price other than the two that are real.
 */

const FEATURES: { icon: string; title: string; text: string; free: boolean }[] = [
  {
    icon: 'ph-calendar-blank',
    title: 'The counted month',
    text: 'Sabbaths, feasts and new months, worked out for where you live. The day turns at your sunset.',
    free: true,
  },
  {
    icon: 'ph-drop',
    title: 'Cycle tracking',
    text: 'Log your cycle and see predictions. Pregnancy and postpartum are covered, with the Leviticus 15 and 12 counts shown as study. None of it is uploaded.',
    free: true,
  },
  {
    icon: 'ph-book-open',
    title: 'Reading plans',
    text: 'The Torah in 52 weeks, with Enoch and Jubilees alongside, set to the biblical week.',
    free: true,
  },
  {
    icon: 'ph-barbell',
    title: 'Movement',
    text: 'Workouts, timers and a history of what you did. Hands-free voice rep counting is part of membership.',
    free: false,
  },
  {
    icon: 'ph-bowl-food',
    title: 'Recipes',
    text: 'Shared recipes checked against Leviticus 11, with the citation shown. The order shifts as the Sabbath gets closer.',
    free: true,
  },
  {
    icon: 'ph-hands-praying',
    title: 'Sisters',
    text: "A shared prayer board. Post a request, say amen to someone else's, and mark yours answered.",
    free: true,
  },
]

const FREE = [
  'Cycle logging, predictions, pregnancy and postpartum',
  'The Leviticus 15 and 12 counts, shown as study',
  'Every reading plan: Torah in 52 weeks, Enoch, Jubilees',
  'Feast dates and Sabbath times',
  'Journal, unlimited, and private prayers',
  'Workouts, timers, history and daily tracking',
  'Recipes and the Leviticus 11 clean check',
]

const MEMBERSHIP = [
  'Hands-free voice rep counting',
  'Cycle insights, patterns and year in review',
  "A doctor's report, as a PDF",
  'Meal planning for the household',
  'Custom symptoms and tracking tags',
]

export type FrontPage = 'home' | 'table' | 'keepers' | 'calendar' | 'locked' | 'signin'

export function FrontDoor({ page, reckoning }: { page: FrontPage; reckoning: Reckoning | null }) {
  if (page === 'signin') {
    return (
      <main className="wrap auth">
        <LoginForm />
        <p className="fine">
          <Link to="/privacy">Privacy policy</Link>
        </p>
      </main>
    )
  }

  return (
    <main className="wrap main">
      <div className="col-main">
        {page === 'home' && (
          <>
        <div className="hero-row">
        <section className="card card-ambient hero-card">
          <div className="glow-a" aria-hidden="true" />
          <div className="glow-b" aria-hidden="true" />
          <div className="card-kicker">
            <span className="live" aria-hidden="true" />
            <span className="label">A Torah lifestyle companion for women</span>
          </div>
          <h1 className="hero-title">
            Her week, <em>kept</em>.
          </h1>
          <p className="hero-sub">
            Movement, habits, your cycle, the Word and the table, all arranged by the calendar
            Scripture keeps. The day turns at sunset.
          </p>
          <div className="hero-cta">
            <Link className="btn-primary" to="/register">
              Create an account
            </Link>
            <Link className="btn-outline" to="/keepers">
              What membership includes
            </Link>
          </div>
        </section>

        <AppointedTimeCard reckoning={reckoning} />
        </div>

        <section className="card">
          <h2 className="section-title">Most calendars follow Rome.</h2>
          <p className="section-lede">
            Covellah uses the calendar Torahly works out for where you live: twelve numbered
            months, with weeks that end on the seventh day. Feasts fall on their appointed dates,
            and the Sabbath is marked as one of them.
          </p>
          <div className="feature-grid">
            {FEATURES.map((feature) => (
              <div className="feature" key={feature.title}>
                <i className={`ph-duotone ${feature.icon}`} aria-hidden="true" />
                <h4>{feature.title}</h4>
                <p>{feature.text}</p>
                {feature.free && <span className="feature-free">Free, always</span>}
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <h2 className="section-title">What is yours stays on your phone.</h2>
          <p className="section-lede">
            Your journal, cycle, pregnancy records and private prayers are never uploaded, so the
            server never holds them. This site shows them only when your phone sends it a copy,
            and closing the tab deletes that copy.
          </p>
          <div className="feature-grid">
            <div className="feature">
              <i className="ph-duotone ph-device-mobile" aria-hidden="true" />
              <h4>The phone is the record</h4>
              <p>Cycle, journal and pregnancy data stay on your phone. No account holds them.</p>
            </div>
            <div className="feature">
              <i className="ph-duotone ph-lock-key" aria-hidden="true" />
              <h4>Reading it on a laptop</h4>
              <p>Pair your phone on the same network, or move a file across yourself. Nothing goes through a relay.</p>
            </div>
          </div>
          <p className="fine">
            No advertising or profiling, and no third-party analytics.{' '}
            <Link to="/privacy">Read the privacy policy</Link>.
          </p>
        </section>

        <section className="card more-row">
          <Link className="btn-outline" to="/recipes">
            Try the Leviticus 11 check
          </Link>
          <Link className="btn-outline" to="/keepers">
            See what membership includes
          </Link>
        </section>

        <section className="card card-ambient" id="join">
          <div className="glow-a" aria-hidden="true" />
          <h2 className="section-title">Built by one pair of hands.</h2>
          <p className="section-lede">
            There are no investors and no ads, and nothing about the women who use it is sold.
          </p>
          <div className="hero-cta">
            <Link className="btn-primary" to="/register">
              Create an account
            </Link>
          </div>
        </section>
          </>
        )}
        {page === 'table' && (
          <>
        <section className="card">
          <h2 className="section-title">Ask it whether you may eat something.</h2>
          <p className="section-lede">
            Covellah checks each ingredient against Leviticus 11 and shows the citation, so you
            can read the text yourself. There are three answers, because a label that only says
            "gelatin" doesn't tell you whether it came from a pig or a cow.
          </p>
          <CleanCheckDemo />
          <p className="fine">This is the real check, using the same list as the app.</p>
        </section>

          </>
        )}
        {page === 'calendar' && (
          <>
            <AppointedTimeCard reckoning={reckoning} />
            <MonthWidget reckoning={reckoning} />
            <AppointedTimes reckoning={reckoning} />
          </>
        )}
        {page === 'locked' && (
          <section className="card">
            <h2 className="section-title">Sign in to see this page.</h2>
            <p className="section-lede">
              The prayer board and your account are for signed-in members. Use the form on this
              page, or{' '}
              <Link to="/">read what Covellah is</Link> first.
            </p>
          </section>
        )}
        {page === 'keepers' && (
          <>
        <section className="card">
          <div className="card-kicker">
            <span className="label">The Keepers</span>
          </div>
          <h2 className="section-title" style={{ marginTop: 8 }}>
            Everything the Torah asks of you is free.
          </h2>
          <p className="section-lede">
            It always will be. Membership pays for the conveniences and for the server the rest
            runs on.
          </p>
          <div className="offer-split">
            <div>
              <div className="col-h">Free, always</div>
              <ul className="rows-plain">
                {FREE.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="col-h">With membership</div>
              <ul className="rows-plain">
                {MEMBERSHIP.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <p className="price">
                <span className="amount-big">$29.99</span> a year, or $3.99 monthly.
              </p>
              <p className="fine">
                Covellah isn't on the Play Store yet, so there's nothing to unlock today.
              </p>
            </div>
          </div>
        </section>

        <section className="card card-ambient" id="join">
          <div className="glow-a" aria-hidden="true" />
          <h2 className="section-title">Built by one pair of hands.</h2>
          <p className="section-lede">
            There are no investors and no ads, and nothing about the women who use it is sold.
          </p>
          <div className="hero-cta">
            <Link className="btn-primary" to="/register">
              Create an account
            </Link>
          </div>
        </section>
          </>
        )}
      </div>

      <aside className="col-side">
        <section className="card" id="signin">
          <LoginForm />
        </section>
        {page !== 'calendar' && <AppointedTimes reckoning={reckoning} />}
        {page !== 'calendar' && <MonthWidget reckoning={reckoning} />}
      </aside>
    </main>
  )
}
